import type { BoundaryPartRequest } from 'generatedTypes/flight/models';
import { useCallback, useState } from 'react';

import useBoundary from 'hooks/useBoundary';
import useClient from 'hooks/useClient';
import useFarm from 'hooks/useFarm';
import useField from 'hooks/useField';
import useImports from 'hooks/useImports';
import { JOHN_DEERE_SOURCE } from 'hooks/useImports/constants';

import type {
    BoundaryPartRequestBoundaryType,
    ImportResult,
    JohnDeereTreeClient,
    UseJohnDeereImportReturn
} from './types';

const DEFAULT_BOUNDARY_TYPE: BoundaryPartRequestBoundaryType = 'APPLY';

const useJohnDeereImport = (): UseJohnDeereImportReturn => {
    const [isImporting, setIsImporting] = useState(false);
    const [importResults, setImportResults] = useState<ImportResult[]>([]);

    const { createBoundary } = useBoundary();
    const { createClient } = useClient();
    const { createFarm } = useFarm();
    const { assignFieldToBoundary, createField } = useField();
    const { registerImport } = useImports();

    const importSelected = useCallback(
        async ({
            boundaryTypes,
            importedIds,
            jdToSvmIdMap,
            selectedIds,
            treeData
        }: {
            boundaryTypes: Record<string, BoundaryPartRequestBoundaryType>;
            importedIds: Set<string>;
            jdToSvmIdMap: Record<string, string>;
            selectedIds: Set<string>;
            treeData: JohnDeereTreeClient[];
        }) => {
            try {
                setIsImporting(true);
                setImportResults([]);

                const results: ImportResult[] = [];

                for (const client of treeData) {
                    const clientSelected = selectedIds.has(client.id);
                    const clientImported = importedIds.has(client.id);
                    const hasSelectedDescendants = (client.farms ?? []).some(
                        farm =>
                            selectedIds.has(farm.id) ||
                            (farm.fields ?? []).some(
                                field =>
                                    selectedIds.has(field.id) ||
                                    (field.boundaries ?? []).some(boundary =>
                                        selectedIds.has(boundary.id)
                                    )
                            )
                    );

                    if (!clientSelected && !hasSelectedDescendants) {
                        continue;
                    }

                    let clientSvmId: string | undefined;

                    if (clientImported) {
                        clientSvmId = jdToSvmIdMap[client.id];
                    } else if (clientSelected) {
                        try {
                            const response = await createClient({
                                client: {
                                    name: client.name
                                }
                            });

                            clientSvmId = response?.id;

                            if (clientSvmId) {
                                const mapping = await registerImport({
                                    externalId: client.id,
                                    externalSource: JOHN_DEERE_SOURCE,
                                    svmEntityId: clientSvmId,
                                    svmEntityType: 'CLIENT'
                                });

                                results.push({
                                    name: client.name,
                                    success: !!mapping,
                                    type: 'client',
                                    ...(!mapping && { error: 'Created but mapping failed' })
                                });
                            } else {
                                results.push({
                                    error: 'No ID returned',
                                    name: client.name,
                                    success: false,
                                    type: 'client'
                                });
                            }
                        } catch (error) {
                            results.push({
                                error: error instanceof Error ? error.message : 'Unknown error',
                                name: client.name,
                                success: false,
                                type: 'client'
                            });
                        }
                    }

                    if (!clientSvmId) {
                        continue;
                    }

                    for (const farm of client.farms ?? []) {
                        const farmSelected = selectedIds.has(farm.id);
                        const farmImported = importedIds.has(farm.id);
                        const hasSelectedFieldsOrBoundaries = (farm.fields ?? []).some(
                            field =>
                                selectedIds.has(field.id) ||
                                (field.boundaries ?? []).some(boundary =>
                                    selectedIds.has(boundary.id)
                                )
                        );

                        if (!farmSelected && !hasSelectedFieldsOrBoundaries) {
                            continue;
                        }

                        let farmSvmId: string | undefined;

                        if (farmImported) {
                            farmSvmId = jdToSvmIdMap[farm.id];
                        } else if (farmSelected) {
                            try {
                                const response = await createFarm({
                                    clientId: clientSvmId,
                                    farm: {
                                        name: farm.name
                                    }
                                });

                                farmSvmId = response?.id;

                                if (farmSvmId) {
                                    const mapping = await registerImport({
                                        externalId: farm.id,
                                        externalSource: JOHN_DEERE_SOURCE,
                                        svmEntityId: farmSvmId,
                                        svmEntityType: 'PARCEL'
                                    });

                                    results.push({
                                        name: farm.name,
                                        success: !!mapping,
                                        type: 'farm',
                                        ...(!mapping && { error: 'Created but mapping failed' })
                                    });
                                } else {
                                    results.push({
                                        error: 'No ID returned',
                                        name: farm.name,
                                        success: false,
                                        type: 'farm'
                                    });
                                }
                            } catch (error) {
                                results.push({
                                    error: error instanceof Error ? error.message : 'Unknown error',
                                    name: farm.name,
                                    success: false,
                                    type: 'farm'
                                });
                            }
                        }

                        if (!farmSvmId) {
                            continue;
                        }

                        for (const field of farm.fields ?? []) {
                            const fieldSelected = selectedIds.has(field.id);
                            const fieldImported = importedIds.has(field.id);
                            const hasSelectedBoundaries = (field.boundaries ?? []).some(boundary =>
                                selectedIds.has(boundary.id)
                            );

                            if (!fieldSelected && !hasSelectedBoundaries) {
                                continue;
                            }

                            let fieldSvmId: string | undefined;

                            if (fieldImported) {
                                fieldSvmId = jdToSvmIdMap[field.id];
                            } else if (fieldSelected) {
                                try {
                                    const response = await createField({
                                        farmId: farmSvmId,
                                        field: {
                                            name: field.name
                                        }
                                    });

                                    fieldSvmId = response?.id;

                                    if (fieldSvmId) {
                                        const mapping = await registerImport({
                                            externalId: field.id,
                                            externalSource: JOHN_DEERE_SOURCE,
                                            svmEntityId: fieldSvmId,
                                            svmEntityType: 'SITE'
                                        });

                                        results.push({
                                            name: field.name,
                                            success: !!mapping,
                                            type: 'field',
                                            ...(!mapping && { error: 'Created but mapping failed' })
                                        });
                                    } else {
                                        results.push({
                                            error: 'No ID returned',
                                            name: field.name,
                                            success: false,
                                            type: 'field'
                                        });
                                    }
                                } catch (error) {
                                    results.push({
                                        error:
                                            error instanceof Error
                                                ? error.message
                                                : 'Unknown error',
                                        name: field.name,
                                        success: false,
                                        type: 'field'
                                    });
                                }
                            }

                            for (const boundary of field.boundaries ?? []) {
                                if (!selectedIds.has(boundary.id)) {
                                    continue;
                                }

                                if (importedIds.has(boundary.id)) {
                                    continue;
                                }

                                try {
                                    const boundaryParts = buildBoundaryParts({
                                        boundary,
                                        boundaryType:
                                            boundaryTypes[boundary.id] ?? DEFAULT_BOUNDARY_TYPE
                                    });

                                    const boundaryResp = await createBoundary({
                                        boundary: {
                                            boundaryParts:
                                                boundaryParts as unknown as BoundaryPartRequest[],
                                            name: boundary.name
                                        }
                                    });

                                    if (boundaryResp?.id && fieldSvmId) {
                                        await assignFieldToBoundary({
                                            boundaryId: boundaryResp.id,
                                            siteId: fieldSvmId
                                        });

                                        const mapping = await registerImport({
                                            externalId: boundary.id,
                                            externalSource: JOHN_DEERE_SOURCE,
                                            svmEntityId: boundaryResp.id,
                                            svmEntityType: 'BOUNDARY'
                                        });

                                        results.push({
                                            name: boundary.name,
                                            success: !!mapping,
                                            type: 'boundary',
                                            ...(!mapping && { error: 'Created but mapping failed' })
                                        });
                                    } else {
                                        results.push({
                                            error: 'No ID returned',
                                            name: boundary.name,
                                            success: false,
                                            type: 'boundary'
                                        });
                                    }
                                } catch (error) {
                                    results.push({
                                        error:
                                            error instanceof Error
                                                ? error.message
                                                : 'Unknown error',
                                        name: boundary.name,
                                        success: false,
                                        type: 'boundary'
                                    });
                                }
                            }
                        }
                    }
                }

                setImportResults(results);
            } finally {
                setIsImporting(false);
            }
        },
        [
            assignFieldToBoundary,
            createBoundary,
            createClient,
            createFarm,
            createField,
            registerImport
        ]
    );

    return {
        importResults,
        importSelected,
        isImporting
    };
};

const buildBoundaryParts = ({
    boundary,
    boundaryType
}: {
    boundary: {
        multipolygons: { rings: { points: { lat: number; lon: number }[]; type: string }[] }[];
    };
    boundaryType: BoundaryPartRequestBoundaryType;
}): {
    boundary: { coordinates: number[][][]; type: string };
    boundaryType: BoundaryPartRequestBoundaryType;
    geometryType: string;
}[] => {
    const parts: {
        boundary: { coordinates: number[][][]; type: string };
        boundaryType: BoundaryPartRequestBoundaryType;
        geometryType: string;
    }[] = [];

    if (!boundary.multipolygons?.length) {
        return parts;
    }

    const exteriorRing = boundary.multipolygons[0].rings.find(ring => ring.type === 'exterior');

    if (exteriorRing) {
        const coordinates: number[][][] = [
            exteriorRing.points.map(point => [point.lon, point.lat])
        ];

        const interiorRings = boundary.multipolygons[0].rings.filter(
            ring => ring.type === 'interior'
        );

        for (const ring of interiorRings) {
            coordinates.push(ring.points.map(point => [point.lon, point.lat]));
        }

        parts.push({
            boundary: { coordinates, type: 'Polygon' },
            boundaryType,
            geometryType: 'POLYGON'
        });
    }

    return parts;
};

export default useJohnDeereImport;
